import React, { useState, useEffect, useRef } from 'react';
import HomePage from './components/HomePage';
import DropOffForm from './components/DropOffForm';
import PickupForm from './components/PickupForm';
import AdminPanel from './components/AdminPanel';
import AdminLogin from './components/AdminLogin';
import LockerSelection from './components/LockerSelection';
import { initializeLockers } from './data/firebaseService';
import './App.css';

function App() {
  const [currentView, setCurrentView] = useState('home');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedLocker, setSelectedLocker] = useState('');
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  
  // Auto-redirect timer (5 minutes = 300000ms)
  const TIMEOUT_DURATION = 300000; // 5 minutes
  const WARNING_DURATION = 10000; // 10 seconds warning
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  // Initialize Firebase data when app starts
  useEffect(() => {
    const initFirebase = async () => {
      try {
        await initializeLockers();
        console.log('Firebase initialized successfully');
      } catch (error) {
        console.error('Error initializing Firebase:', error);
      }
    };
    
    initFirebase();
  }, []);

  // Auto-redirect functionality
  useEffect(() => {
    const resetTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        window.location.href = 'https://www.domtom.in/';
      }, TIMEOUT_DURATION);
    };

    // Set up event listeners for user activity
    const handleUserActivity = () => {
      resetTimer();
    };

    // Add event listeners for user interactions
    document.addEventListener('click', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);
    document.addEventListener('mousemove', handleUserActivity);
    document.addEventListener('scroll', handleUserActivity);
    document.addEventListener('touchstart', handleUserActivity);

    // Start the timer
    resetTimer();

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener('click', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
      document.removeEventListener('mousemove', handleUserActivity);
      document.removeEventListener('scroll', handleUserActivity);
      document.removeEventListener('touchstart', handleUserActivity);
    };
  }, []);

  const handleNavigate = (view, location = '') => {
    setCurrentView(view);
    if (location) {
      setSelectedLocation(location);
    }
  };

  const handleBack = () => {
    setCurrentView('home');
    setSelectedLocation('');
    setSelectedLocker('');
    // Reset admin login when going back to home
    if (currentView === 'admin') {
      setIsAdminLoggedIn(false);
    }
  };

  const handleAdminLogin = () => {
    setIsAdminLoggedIn(true);
    setCurrentView('admin');
  };

  const handleLockerSelect = (location, lockerId) => {
    setSelectedLocker(lockerId);
    setCurrentView('dropoff');
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'lockerselection':
        return (
          <LockerSelection
            location={selectedLocation}
            onBack={handleBack}
            onLockerSelect={handleLockerSelect}
          />
        );
      case 'dropoff':
        return (
          <DropOffForm
            location={selectedLocation}
            selectedLocker={selectedLocker}
            onBack={handleBack}
          />
        );
      case 'pickup':
        return (
          <PickupForm
            onBack={handleBack}
          />
        );
      case 'admin':
        if (isAdminLoggedIn) {
          return (
            <AdminPanel
              onBack={handleBack}
            />
          );
        } else {
          return (
            <AdminLogin
              onLogin={handleAdminLogin}
              onBack={handleBack}
            />
          );
        }
      default:
        return (
          <HomePage
            onNavigate={handleNavigate}
          />
        );
    }
  };

  return (
    <div className="App">
      {renderCurrentView()}
    </div>
  );
}

export default App;
